import { useMutation } from "@tanstack/react-query";
import { saveDocxForm, submitDocxForm } from "../../api/endpoints/docxFormEndpoints";
import { updateFormStatus } from "../../api/endpoints/fetchTemplates";
import type { Template } from "../../api/endpoints/fetchTemplates";
import type { DocxFormData } from "./collectDocxFormValues";

type MinimalUser = { email?: string; role?: "admin" | "member" } | null;

export function useSubmitDocxFormMutation(params: {
  template: Template;
  user: MinimalUser;
  email: string;
  successTimerRef: React.MutableRefObject<number | null>;
  setShowSuccess: (v: boolean) => void;
  setErrorText: (v: string | null) => void;
  onSaved: (fileId: string) => void;
  onClose?: () => void;
}) {
  const { template, user, email, successTimerRef, setShowSuccess, setErrorText, onSaved, onClose } = params;

  return useMutation({
    networkMode: "always",
    mutationFn: async ({ payload }: { payload: DocxFormData }) => {
      if (!template?.fileId) throw new Error("Missing fileId for submit operation.");
      
      // Save and submit in parallel
      const submitResult = await 
        submitDocxForm(template.cacheId ?? template.fileId, payload, user?.role === "admin")
      return submitResult;
    },
    onMutate: () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
        successTimerRef.current = null;
      }
      setShowSuccess(false);
      setErrorText(null);
    },
    onSuccess: async () => {
      if (template?.fileId) {
        try {
          await updateFormStatus(template.htmlId, email, false);
        } catch (publishErr) {
          console.error("Failed to update form status after submit:", publishErr);
        }
      }
      setShowSuccess(true);
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
      successTimerRef.current = window.setTimeout(() => {
        setShowSuccess(false);
        if (template?.fileId) onSaved(template.fileId);
        if (onClose) onClose();
      }, 2000);
    },
    onError: (err: unknown) => {
      setShowSuccess(false);
      let message = "Failed to submit";
      if (typeof err === "object" && err !== null) {
        const maybeWithResponse = err as { response?: { data?: { message?: string } } };
        const maybeWithMessage = err as { message?: string };
        message = maybeWithResponse.response?.data?.message ?? maybeWithMessage.message ?? message;
      }
      setErrorText(message);
    },
  });
}
